import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";

import { BulbIcon } from "@/app/assets/icons/interface/interface";
import Spinner from "@/app/components/Spinner";

interface LangContent {
	en: React.ReactNode;
	sw: React.ReactNode;
}

interface RoleExplainerAccordionProps {
	title: string;
	content: LangContent;
	loading?: boolean;
}

function RoleExplainerAccordion({ title, content, loading }: RoleExplainerAccordionProps) {
	// start closed if loading, otherwise open
	const [open, setOpen] = useState<boolean>(() => !loading);
	const [lang, setLang] = useState<"en" | "sw">("en");

	// keep accordion closed while loading and open automatically when loading completes
	useEffect(() => {
		if (typeof loading === "boolean") setOpen(!loading);
	}, [loading]);

	const toggleOpen = () => setOpen((v) => !v);

	const handleLangToggle = (e: React.MouseEvent, next?: "en" | "sw") => {
		e.stopPropagation(); // prevent accordion toggle
		const newLang = next ?? (lang === "en" ? "sw" : "en");
		setLang(newLang);
	};

	return (
		<div
			className="w-full border rounded-lg border-[#C9DBF0] shadow-sm"
			style={{ background: "#EFF6FF", borderColor: "#E6F0FF" }}
		>
			{/* Top header bar */}
			<div
				className="flex items-center justify-between px-3 py-2 rounded-t-lg"
				style={{ background: "#E6F2FF", color: "#0B4F9E" }}
			>
				<div className="flex items-center gap-2">
					<span className="text-lg">
						<BulbIcon />
					</span>
					<span className="font-medium text-sm flex items-center gap-2">
						{title}
						{loading && <Spinner />}
					</span>
				</div>

				<div className="flex items-center gap-4">
					{/* SMALL Pill switch - reduced size */}
					<div className="select-none">
						<button
							type="button"
							role="switch"
							aria-checked={lang === "sw"}
							onClick={(e) => handleLangToggle(e)}
							className="w-12 h-6 bg-[#012B99] rounded-full border border-[#E6EEF9] flex items-center p-1 relative focus:outline-none shadow-sm"
							aria-label="Toggle language"
						>
							{/* track */}
							<div className="absolute inset-0 rounded-full" style={{ backgroundColor: "#0a6fe2" }} aria-hidden />

							{/* labels inside track */}
							<div className="absolute inset-0 flex items-center justify-between px-2 pointer-events-none">
								<span className={`text-[8px] font-medium ${lang === "sw" ? "text-white" : "opacity-0"}`}>SW</span>
								<span className={`text-[8px] font-medium ${lang === "en" ? "text-white" : "opacity-0"}`}>EN</span>
							</div>

							{/* small knob */}
							<div
								className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-all duration-200 ${
									lang === "sw" ? "right-1" : "left-1"
								}`}
								style={{ pointerEvents: "none" }}
							/>
						</button>
					</div>

					{/* Chevron button controls accordion open/close */}
					<button
						type="button"
						onClick={toggleOpen}
						aria-expanded={open}
						aria-disabled={loading}
						className={`p-1 rounded-full hover:bg-[#DCEFFD] transition-transform ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
						style={{ color: "#0B4F9E" }}
						disabled={!!loading}
					>
						<ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
					</button>
				</div>
			</div>

			{/* Accordion (controlled) */}
			<Accordion.Root
				type="single"
				collapsible
				value={open ? "item-1" : undefined}
				onValueChange={(v) => setOpen(Boolean(v))}
			>
				<Accordion.Item value="item-1" className="rounded-b-lg overflow-hidden">
					<Accordion.Content
						className="text-[#2B2B2B] text-sm leading-snug px-2 py-2 space-y-3 data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown"
						style={{ background: "#F8FAFF", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}
					>
						<div className="bg-white p-4 rounded-md text-sm">{lang === "en" ? content.en : content.sw}</div>
					</Accordion.Content>
				</Accordion.Item>
			</Accordion.Root>
		</div>
	);
}

export default RoleExplainerAccordion;
